import React, {createContext, useCallback, useEffect, useState} from "react";
import toast from "react-hot-toast";
import API_BASE_URL from '../config';

export const ShopContext = createContext(null);

const getDefaultCart = () => {
    return {};
}

const getCartKey = (itemId, size = '') => {
    const numericId = Number(itemId);
    return size && size !== '' ? `${numericId}-${size}` : numericId.toString();
}

const getProductIdFromKey = (cartKey) => {
    return parseInt(cartKey.split('-')[0]);
}

const getSizeFromKey = (cartKey) => {
    const parts = cartKey.split('-');
    return parts.length > 1 ? parts[1] : '';
}

const ShopContextProvider = (props) => {
    const [all_product, setAll_Product] = useState([]); 
    const [allProducts, setAllProducts] = useState([]);
    const [cartItems, setCartItems] = useState(getDefaultCart());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cartCount, setCartCount] = useState(0);
    const [wishlist, setWishlist] = useState([]);
    const [recentlyViewed, setRecentlyViewed] = useState([]);

    const showMessage = useCallback((text, type = 'success') => {
        const options = {
            duration: 4000,
        };

        if (type === 'success') {
            toast.success(text, options);
        } else if (type === 'error') {
            toast.error(text, options);
        } else if (type === 'warning') {
            toast.error(text, options);
        } else {
            toast(text, options);
        }
    }, []);

    // Function to fetch cart count
    const fetchCartCount = async () => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/getcart`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({}),
                });
                
                if (response.ok) {
                    const cartData = await response.json();
                    let count = 0;
                    Object.values(cartData.cartData || {}).forEach(qty => {
                        count += qty;
                    });
                    setCartCount(count);
                }
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    const response = await fetch(`${API_BASE_URL}/getcart`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                    
                    if (response.ok) {
                        const cartData = await response.json();
                        let count = 0;
                        Object.values(cartData.cartData || {}).forEach(qty => {
                            count += qty;
                        });
                        setCartCount(count);
                    }
                }
            }
        } catch (error) {
        }
    };

    // Fetch products for the shop
    const fetchShopProducts = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/allproducts`);
            
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const data = await response.json();
            
            let productsArray = [];
            
            if (Array.isArray(data)) {
                productsArray = data;
            } else if (data && Array.isArray(data.products)) {
                productsArray = data.products;
            } else if (data && data.success && Array.isArray(data.products)) {
                productsArray = data.products;
            } else {
                productsArray = [];
            }
            
            setAll_Product(productsArray);
            return productsArray;
            
        } catch (err) {
            setError(`Backend connection failed: ${err.message}`);
            setAll_Product([]);
            showMessage('Failed to load products. Please check your connection.', 'error');
            return [];
        }
    }, [showMessage]);

    // Client-side search function
    const performClientSideSearch = (query) => {
        if (!allProducts || allProducts.length === 0) {
            return [];
        }
        
        const lowerQuery = query.toLowerCase();
        
        const results = allProducts.filter(product => {
            const name = product.name?.toLowerCase() || '';
            const category = product.category?.toLowerCase() || '';
            const description = product.description?.toLowerCase() || '';
            
            return (
                name.includes(lowerQuery) ||
                category.includes(lowerQuery) ||
                description.includes(lowerQuery)
            );
        });
        
        return results.slice(0, 10);
    };

    // Search products via API
    const searchProducts = async (query) => {
        try {
            
            if (!query || query.trim().length < 1) {
                return [];
            }
            
            const response = await fetch(`${API_BASE_URL}/api/products/search?q=${encodeURIComponent(query.trim())}`);
            
            if (!response.ok) {
                return performClientSideSearch(query);
            }
            
            const data = await response.json();
            
            if (data.success) {
                return data.products || [];
            } else {
                return performClientSideSearch(query);
            }
            
        } catch (error) {
            return performClientSideSearch(query);
        }
    };

    // Get quick search suggestions
    const getSearchSuggestions = async (query) => {
        try {
            if (!query || query.trim().length < 2) {
                return [];
            }
            
            const response = await fetch(`${API_BASE_URL}/api/products/quick-search?q=${encodeURIComponent(query.trim())}`);
            
            if (response.ok) {
                const data = await response.json();
                return data.suggestions || [];
            }
            return [];
        } catch (error) {
            return [];
        }
    };

    const fetchUserCart = useCallback(async (products) => {
        try {
            let cartData = {};
            
            if(localStorage.getItem('auth-token')){
                const response = await fetch(`${API_BASE_URL}/getcart`,{
                    method:'POST',
                    headers:{
                        'Accept':'application/json',
                        'auth-token':`${localStorage.getItem('auth-token')}`,
                        'Content-Type':'application/json',
                    },
                    body: JSON.stringify({}),
                });
                
                if (response.ok) {
                    const result = await response.json();
                    cartData = result.cartData || {};
                }
            } else {
                let sessionId = localStorage.getItem('guest-session-id');
                
                if (sessionId) {
                    const response = await fetch(`${API_BASE_URL}/getcart`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                    
                    if (response.ok) {
                        const result = await response.json();
                        cartData = result.cartData || {};
                    }
                } else {
                    const sessionResponse = await fetch(`${API_BASE_URL}/api/guest/session`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    if (sessionResponse.ok) {
                        const sessionData = await sessionResponse.json();
                        if (sessionData.success) {
                            sessionId = sessionData.sessionId;
                            localStorage.setItem('guest-session-id', sessionId);
                        }
                    }
                }
            }
            
            const validatedCart = {};
            
            Object.keys(cartData).forEach(cartKey => {
                const productId = getProductIdFromKey(cartKey);
                
                const productExists = products.some(product => 
                    product.id === productId
                );
                
                if (productExists && cartData[cartKey] > 0) {
                    validatedCart[cartKey] = cartData[cartKey];
                }
            });
            
            setCartItems(validatedCart);
            
            let count = 0;
            Object.values(validatedCart).forEach(qty => {
                count += qty;
            });
            setCartCount(count);
            
        } catch (err) {
            setCartItems(getDefaultCart());
            showMessage('Failed to load cart data.', 'error');
        }
    }, [showMessage]);

    // NEW: Fetch wishlist
    const fetchWishlist = useCallback(async () => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/api/wishlist`, {
                    method: 'GET',
                    headers: {
                        'auth-token': localStorage.getItem('auth-token'),
                    },
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setWishlist(data.wishlist || []);
                    }
                }
            }
        } catch (error) {
        }
    }, []);

    // NEW: Add to wishlist
    const addToWishlist = async (productId) => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/api/wishlist/add`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                    },
                    body: JSON.stringify({ productId }),
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setWishlist(prev => [...prev, productId]);
                        showMessage('Added to wishlist!', 'success');
                        return true;
                    }
                }
            } else {
                showMessage('Please login to add items to wishlist', 'warning');
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    // NEW: Remove from wishlist
    const removeFromWishlist = async (productId) => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/api/wishlist/remove`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                    },
                    body: JSON.stringify({ productId }),
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setWishlist(prev => prev.filter(id => id !== productId));
                        showMessage('Removed from wishlist', 'info');
                        return true;
                    }
                }
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    // NEW: Check if product is in wishlist
    const isInWishlist = (productId) => {
        return wishlist.includes(productId);
    };

    // NEW: Add to recently viewed
    const addToRecentlyViewed = (product) => {
        try {
            const current = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
            const filtered = current.filter(p => p.id !== product.id);
            const updated = [product, ...filtered].slice(0, 10);
            localStorage.setItem('recentlyViewed', JSON.stringify(updated));
            setRecentlyViewed(updated);
        } catch (error) {
        }
    };

    // Initialize the app
    useEffect(() => {
        const initializeApp = async () => {
            try {
                
                const shopProducts = await fetchShopProducts();
                setAllProducts(shopProducts);
                
                await fetchUserCart(shopProducts);
                await fetchWishlist();
                
                // Load recently viewed
                const stored = localStorage.getItem('recentlyViewed');
                if (stored) {
                    setRecentlyViewed(JSON.parse(stored));
                }
                
            } catch (error) {
                showMessage('Failed to initialize app. Please refresh the page.', 'error');
            } finally {
                setLoading(false);
            }
        };

        initializeApp();
    }, [fetchShopProducts, fetchUserCart, fetchWishlist, showMessage]);

    // Refresh all products
    const refreshAllProducts = async () => {
        try {
            setLoading(true);
            
            const shopProducts = await fetchShopProducts();
            setAllProducts(shopProducts);
            
            showMessage('Products refreshed successfully!', 'success');
        } catch (error) {
            showMessage('Failed to refresh products', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Add to cart function
    const addToCart = async (itemId, size = '', color = '') => {
        try {
            const numericItemId = Number(itemId);
            const product = all_product.find(p => p.id === numericItemId);
            
            if (!product) {
                showMessage('Product not found. Please try again.', 'error');
                return false;
            }

            let response;
            let cartKey = getCartKey(numericItemId, size);
            
            if (localStorage.getItem('auth-token')) {
                response = await fetch(`${API_BASE_URL}/addtocart`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token')
                    },
                    body: JSON.stringify({ 
                        itemId: numericItemId,
                        size: size,
                        color: color
                    })
                });
            } else {
                let sessionId = localStorage.getItem('guest-session-id');
                if (!sessionId) {
                    const sessionResponse = await fetch(`${API_BASE_URL}/api/guest/session`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    const sessionData = await sessionResponse.json();
                    if (sessionData.success) {
                        sessionId = sessionData.sessionId;
                        localStorage.setItem('guest-session-id', sessionId);
                    }
                }
                
                response = await fetch(`${API_BASE_URL}/addtocart`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'session-id': sessionId
                    },
                    body: JSON.stringify({ 
                        itemId: numericItemId,
                        size: size,
                        color: color
                    })
                });
            }
            
            const data = await response.json();
            
            if (data.success) {
                setCartItems(prev => {
                    const currentQuantity = prev[cartKey] || 0;
                    const newQuantity = currentQuantity + 1;
                    const updatedCart = { 
                        ...prev, 
                        [cartKey]: newQuantity 
                    };
                    
                    setCartCount(prevCount => prevCount + 1);
                    
                    return updatedCart;
                });
                
                showMessage(`${product.name} (Size: ${size || 'One Size'}) has been added to cart!`, 'success');
                return true;
            } else {
                showMessage(data.error || 'Failed to add item to cart. Please try again.', 'error');
                return false;
            }
            
        } catch (error) {
            showMessage('An error occurred while adding to cart', 'error');
            return false;
        }
    };

    const removeFromCart = (itemId, size = '') => {
        const numericItemId = Number(itemId);
        const cartKey = getCartKey(numericItemId, size);
        const product = all_product.find(p => p.id === numericItemId);
        
        setCartItems((prev) => {
            const currentQuantity = prev[cartKey] || 0;
            
            if (currentQuantity <= 0) {
                return prev;
            }
            
            const newQuantity = Math.max(0, currentQuantity - 1);
            const updatedCart = { ...prev };
            
            if (newQuantity === 0) {
                delete updatedCart[cartKey];
                setCartCount(prevCount => Math.max(0, prevCount - 1));
            } else {
                updatedCart[cartKey] = newQuantity;
            }

            const updateBackend = async () => {
                try {
                    if (localStorage.getItem('auth-token')) {
                        await fetch(`${API_BASE_URL}/removefromcart`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'auth-token': localStorage.getItem('auth-token'),
                            },
                            body: JSON.stringify({ 
                                itemId: numericItemId,
                                size: size
                            }),
                        });
                    } else {
                        const sessionId = localStorage.getItem('guest-session-id');
                        if (sessionId) {
                            await fetch(`${API_BASE_URL}/removefromcart`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'session-id': sessionId,
                                },
                                body: JSON.stringify({ 
                                    itemId: numericItemId,
                                    size: size
                                }),
                            });
                        }
                    }
                    
                    if (product) {
                        if (newQuantity === 0) {
                            showMessage(`${product.name} (Size: ${size || 'One Size'}) removed from cart`, 'info');
                        } else {
                            showMessage(`Quantity updated for ${product.name} (Size: ${size || 'One Size'})`, 'info');
                        }
                    }
                } catch (err) {
                    showMessage('Failed to update cart. Please try again.', 'error');
                }
            };
            
            updateBackend();
            
            return updatedCart;
        });
    };

    const getTotalCartAmount = () => {
        if (!cartItems || !all_product.length) return 0;
        
        let totalAmount = 0;
        
        for(const cartKey in cartItems) {
            const quantity = cartItems[cartKey];
            if(quantity > 0) {
                const productId = getProductIdFromKey(cartKey);
                const itemInfo = all_product.find((product) => product.id === productId);
                
                if (itemInfo && itemInfo.new_price) {
                    totalAmount += itemInfo.new_price * quantity;
                }
            }
        }
        
        return totalAmount;
    };

    const getTotalCartItems = () => {
        return cartCount;
    };

    const getCartItemsDetails = () => {
        if (!cartItems || !all_product.length) {
            return [];
        }
        
        const cartDetails = [];
        
        for(const cartKey in cartItems) {
            const quantity = cartItems[cartKey];
            if(quantity > 0) {
                const productId = getProductIdFromKey(cartKey);
                const itemInfo = all_product.find((product) => product.id === productId);
                
                if (itemInfo) {
                    const size = getSizeFromKey(cartKey);
                    
                    cartDetails.push({
                        product: itemInfo,
                        quantity: quantity,
                        size: size,
                        cartKey: cartKey
                    });
                }
            }
        }
        
        return cartDetails;
    };

    const getCartItemQuantity = (itemId, size = '') => {
        const cartKey = getCartKey(itemId, size);
        const quantity = cartItems[cartKey] || 0;
        return quantity;
    };

    const updateCartFromBackend = async () => {
        try {
            let response;
            
            if (localStorage.getItem('auth-token')) {
                response = await fetch(`${API_BASE_URL}/getcart`, {
                    method:'POST',
                    headers:{
                        'Accept':'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                        'Content-Type':'application/json',
                    },
                    body: JSON.stringify({}),
                });
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    response = await fetch(`${API_BASE_URL}/getcart`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                }
            }
            
            if (response && response.ok) {
                const result = await response.json();
                const cartData = result.cartData || {};
                const validatedCart = {};
                
                Object.keys(cartData).forEach(cartKey => {
                    const productId = getProductIdFromKey(cartKey);
                    const productExists = all_product.some(product => 
                        product.id === productId
                    );
                    
                    if (productExists && cartData[cartKey] > 0) {
                        validatedCart[cartKey] = cartData[cartKey];
                    }
                });
                
                setCartItems(validatedCart);
                
                let count = 0;
                Object.values(validatedCart).forEach(qty => {
                    count += qty;
                });
                setCartCount(count);
                
                showMessage('Cart updated successfully!', 'success');
            }
        } catch (err) {
            showMessage('Failed to update cart. Please try again.', 'error');
        }
    };

    const clearCart = async () => {
        try {
            setCartItems(getDefaultCart());
            setCartCount(0);
            
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/clearcart`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token')
                    }
                });
                
                const data = await response.json();
                if (!data.success) {
                    throw new Error('Backend clearance failed');
                }
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    const response = await fetch(`${API_BASE_URL}/clearcart`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'session-id': sessionId
                        }
                    });
                    
                    const data = await response.json();
                    if (!data.success) {
                        throw new Error('Backend clearance failed');
                    }
                }
            }
            
            showMessage('Cart cleared successfully!', 'info');
            return true;
        } catch (error) {
            showMessage('Failed to clear cart. Please try again.', 'error');
            return false;
        }
    };

    // Place order function
    const placeOrder = async (orderData) => {
        try {
            let headers = {
                'Content-Type': 'application/json'
            };
            
            if (localStorage.getItem('auth-token')) {
                headers['auth-token'] = localStorage.getItem('auth-token');
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    headers['session-id'] = sessionId;
                }
            }
            
            const response = await fetch(`${API_BASE_URL}/api/order/createorder`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(orderData)
            });

            const data = await response.json();
            
            if (data.success) {
                await clearCart();
                return { success: true, orderId: data.orderId, userType: data.userType };
            } else {
                throw new Error(data.message || 'Failed to place order');
            }
        } catch (error) {
            throw error;
        }
    };

    const clearError = () => {
        setError(null);
    };

    // Get detailed cart items with product info
    const getDetailedCartItems = async () => {
        try {
            let response;
            
            if (localStorage.getItem('auth-token')) {
                response = await fetch(`${API_BASE_URL}/getcartdetails`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({}),
                });
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    response = await fetch(`${API_BASE_URL}/getcartdetails`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                }
            }
            
            if (response && response.ok) {
                const data = await response.json();
                return data.cartItems || [];
            }
            return [];
        } catch (error) {
            return [];
        }
    };

    // Function to get product by ID
    const getProductById = (id) => {
        const numericId = Number(id);
        return all_product.find(product => product.id === numericId);
    };

    // Function to get products by category
    const getProductsByCategory = (category) => {
        return all_product.filter(product => 
            product.category.toLowerCase() === category.toLowerCase() ||
            (product.category_slug && product.category_slug === category)
        );
    };

    // Function to get latest products
    const getLatestProducts = (limit = 8) => {
        return [...all_product]
            .sort((a, b) => b.id - a.id)
            .slice(0, limit);
    };

    // Function to get featured products
    const getFeaturedProducts = (limit = 8) => {
        return all_product
            .filter(product => product.isFeatured)
            .slice(0, limit);
    };

    // Function to get discounted products
    const getDiscountedProducts = (limit = 8) => {
        return all_product
            .filter(product => product.old_price > product.new_price)
            .sort((a, b) => {
                const discountA = ((a.old_price - a.new_price) / a.old_price) * 100;
                const discountB = ((b.old_price - b.new_price) / b.old_price) * 100;
                return discountB - discountA;
            })
            .slice(0, limit);
    };

    // Function to get similar products
    const getSimilarProducts = (product, limit = 4) => {
        return all_product
            .filter(p => 
                p.id !== product.id && 
                p.category === product.category
            )
            .slice(0, limit);
    };

    const contextValue = {
        // Cart related
        getTotalCartItems, 
        getTotalCartAmount, 
        cartItems, 
        addToCart, 
        removeFromCart,
        updateCartFromBackend,
        clearCart,
        getCartItemsDetails,
        getCartItemQuantity,
        getCartKey,
        getProductIdFromKey,
        placeOrder,
        getDetailedCartItems,
        cartCount,
        fetchCartCount,
        
        // Products related
        all_product, 
        allProducts,
        loading,
        error,
        clearError,
        showMessage,
        
        // Search functions
        searchProducts,
        getSearchSuggestions,
        performClientSideSearch,
        
        // Product utility functions
        getProductById,
        getProductsByCategory,
        getLatestProducts,
        getFeaturedProducts,
        getDiscountedProducts,
        getSimilarProducts,
        
        // Refresh function
        refreshAllProducts,
        
        // NEW: Wishlist functions
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        fetchWishlist,
        
        // NEW: Recently viewed
        recentlyViewed,
        addToRecentlyViewed
    };
    
    return (
        <ShopContext.Provider value={contextValue}>
            {props.children}
        </ShopContext.Provider>
    )
}

export default ShopContextProvider;

